/**
 * @flow
 * Created by neo on 05.03.17.
 */
import mobx, {observable, action, computed} from 'mobx';
import Passenger from './Passenger';
import Payment from '../Payment';
import BackendFactory from '../../Services/BackendFactory';
import Address from '../Address';
import Voucher from '../Voucher';
import PromoCode from '../PromoCode';
import moment from 'moment-timezone';

export default class AbstractBookingRequest {
  @observable passengers: Array<Passenger> = [];
  @observable first_name: string;
  @observable last_name: string;
  @observable email: string;
  @observable phone: string;
  @observable note: string;
  @observable payment: Payment;
  @observable check_safety_info: boolean = false;
  @observable check_insurance_info: boolean = false;
  @observable address: Address = new Address();
  @observable voucher: Voucher = new Voucher();
  @observable carry_on_count: number = 0;
  @observable suitcase_count: number = 0;
  @observable remaining_price: number;
  @observable price_explanation: Array<Object>;
  @observable is_exclusive: boolean = false;
  @observable promo_code: PromoCode = new PromoCode();

  static getPromoCodeStorageKey() {
    return 'promo_code';
  }

  constructor(json?: any) {
    if(json) {
      this.start_time = json.start_time;
      this.first_name = json.first_name;
      this.last_name = json.last_name;
      this.email = json.email;
      this.phone = json.phone;
      this.note = json.note;
      this.passengers = json.passengers ? json.passengers.map(p => new Passenger(p)) : [];
      this.payment_types = (json.payment_types || []).map(p => new Payment(p));
      this.payment = new Payment(json.payment || this.payment_types[0]);
      this.check_insurance_info = json.check_insurance_info || false;
      this.check_safety_info = json.check_safety_info || false;
      this.address = new Address(json.address);
      this.voucher = new Voucher(json.voucher);
      this.carry_on_count = json.carry_on_count;
      this.suitcase_count = json.suitcase_count;
      this.is_exclusive = json.is_exclusive;
      this.promo_code = new PromoCode(json.promo_code);

      if (this.passengers.length > 0) {
        this.updatePassengerInfo();
      }
    }

    // get promo code from local storage if present
    if (!this.promo_code.code) {
      const key = AbstractBookingRequest.getPromoCodeStorageKey();
      if (localStorage && localStorage.getItem(key)) {
        this.promo_code.code = localStorage.getItem(key);
      }
    }
  }

  @action setPassengerCount(count: number) {
    if(this.passengerCount < count) {
      for(let i = this.passengerCount; i < count; i++) {
        this.passengers.push(new Passenger());
      }
    } else if(this.passengerCount > count) {
      this.passengers.splice(count - 1, this.passengerCount - count);
    }

    if (+count === +this.aircraft.passengers_capacity) {
      this.is_exclusive = true;
    } else {
      this.is_exclusive = false;
    }
  }

  @computed get maxPassengers(): number {
    return this.aircraft.passengers_capacity;
  }

  @computed get minPassengers(): number {
    return this.aircraft.passengers_min;
  }

  @computed get maxNonExclusivePassengers(): number {
    return this.maxPassengers - 1;
  }

  @computed get passengerCount(): number {
    return this.passengers.length;
  }

  @computed get validPassengers(): boolean {
    if (this.passengerCount > 0) {
      const invalidPassanger = this.passengers.find(p => !p.first_name || !p.last_name || !p.weight);
      if (invalidPassanger) {
        return false;
      }
    }

    return true;
  }

  @computed get actualPrice(): number {
    if (this.remaining_price !== undefined && this.remaining_price !== null) {
      return this.remaining_price;
    }

    return this.actualPriceWithoutDiscount;
  }

  @computed get actualPriceWithoutDiscount(): number {
    if(this.is_exclusive && this.aircraft.price_exclusive) {
      return this.aircraft.price_exclusive;
    }
    const pricePerSeat = this.aircraft.price_passenger;
    return (this.passengers.length || 1) * pricePerSeat;
  }

  @computed get price(): number {
    const pricePerSeat = this.aircraft.price_passenger;
    if (!pricePerSeat) {
      return 0;
    }
    const passengerCount = this.is_exclusive ? this.maxPassengers : (this.passengers.length || 1);
    return passengerCount * pricePerSeat;
  }

  @computed get currency(): string {
    return this.aircraft.currency_code;
  }

  @computed get currencyModel(): string {
    return this.aircraft.currency;
  }

  @computed get currencyRate(): number {
    if (!this.payment.currency) {
      return 1;
    }

    return this.payment.currency.conversion_rate_eur / this.aircraft.currency.conversion_rate_eur;
  }

  @computed get paymentMethodCommission(): number {
    if (!this.payment.fee_fixed && !this.payment.fee_percent) {
      return 0;
    }

    const fixedFee = this.payment.fee_fixed ? this.currencyRate * this.payment.fee_fixed : 0;
    const totalPrice =  Math.ceil((this.actualPrice + fixedFee) / (1 - (this.payment.fee_percent / 100)));
    return totalPrice - this.actualPrice;
  }

  @computed get validEmail(): boolean {
    var re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return re.test(this.email);
  }

  @computed get validPhone(): boolean {
    var re = /^[+]?[0-9 ]{5,}$/;

    return re.test(this.phone);
  }

  @computed get validClient(): boolean {
    return this.first_name && this.last_name &&
      this.phone && this.validEmail;
  }

  @computed get valid(): boolean {
    return this.validClient && this.address.valid && this.check_insurance_info && this.check_safety_info;
  }

  @computed get invalid(): boolean {
    return !this.valid;
  }

  @action resetPayment(): void {
    if (this.payment_types.length > 0) {
      this.payment = new Payment(this.payment_types[0]);
    } else {
      this.payment = new Payment();
    }
  }

  @action removeVoucher(): void {
    this.remaining_price = this.actualPriceWithoutDiscount;
    this.price_explanation = [];
    this.voucher = new Voucher();

    this.applyDiscounts();

    // reset payment to default if it was cleared
    if (!this.payment.id) {
      this.resetPayment();
    }
  }

  @action removePromoCode(): void {
    this.remaining_price = this.actualPriceWithoutDiscount;
    this.price_explanation = [];
    this.promo_code = new PromoCode();

    // remove promo code from local storage if we don't want to use it anymore
    if (localStorage) {
      localStorage.removeItem(AbstractBookingRequest.getPromoCodeStorageKey());
    }

    this.applyDiscounts();

    // reset payment to default if it was cleared
    if (!this.payment.id) {
      this.resetPayment();
    }
  }

  @action async applyDiscounts(): Promise<?string> {
    const backend = await BackendFactory();
    // this.aircraft.id is flight_offer_aircraft.id not aircraft_model.id
    const res = await backend.post('/discounts', {
      booking: {
        ...mobx.toJS(this),
        aircraft: mobx.toJS(this.aircraft)
      }
    });

    if(res.ok) {
      this.remaining_price = +res.data.remaining_price;
      this.price_explanation = res.data.price_explanation || [];

      // only returns valid vouchers, so we don't need to check anything else
      if (res.data.voucher) {
        this.voucher = new Voucher(res.data.voucher);
      }

      if (res.data.promo_code) {
        this.promo_code = new PromoCode(res.data.promo_code);
      }

      if (this.actualPrice == 0) {
        // clear payment information if there's nothing to pay
        this.payment = new Payment();
      } else if (!this.payment.id) {
        // price is > 0 but no payment is selected, select default
        this.resetPayment();
      }
    } else {
      if (res.data && res.data.price_explanation) {
        this.price_explanation = res.data.price_explanation;
        this.remaining_price = undefined;
      }
    }
  }

  @action getPriceToPay(): number {
    return (parseInt((+this.actualPrice + this.paymentMethodCommission) * 100) / 100);
  }

  @action updateFirstName = (name: string) => {
    this.first_name = name;
    this.updatePassengerInfo();
  };

  @action updateLastName = (name: string) => {
    this.last_name = name;
    this.updatePassengerInfo();
  };

  @action updatePassengerInfo = () => {
    if (this.passengers.length > 0) {
      const passenger = this.passengers[0];
      passenger.first_name = this.first_name;
      passenger.last_name = this.last_name;
    }
  };
}



// WEBPACK FOOTER //
// ./src/App/Store/Booking/AbstractBookingRequest.js